
import {Component, Vue, Watch} from "vue-property-decorator";
import ListRequest, {Filter, Ordering, OrderType} from "@/models/ListRequest";
import {mapState} from "vuex";
import {TRANSLATIONS_FETCH} from "@/store/ActionTypes";
import vBreadcrumbs from "@/components/vBreadcrumbs.vue";
import vNutritionLabel from "@/components/vNutritionLabel.vue";
import RecipesRepository from "@/utils/API/RecipesRepository";
import {Recipe} from "@/models/Recipes/Recipe";
import {Nutritions} from "@/models/Recipes/Nutritions";
import vPreLoader from "@/components/vPreLoader.vue";
import DishesRepository from "@/utils/API/DishesRepository";
import {FilterRecipesNames} from "@/models/Recipes/FilterRecipesNames";
import vSelect from "@/components/vSelect.vue";
import vSelectFx from "@/components/vSelectFx.vue";
import {getBoldString, mapNutritions, debounce, getPreviewNutrientsForCountry} from "@/utils/Helpers";

import vModal from "@/components/vModal.vue";
import {RecipeComposition} from "@/models/Recipes/RecipeComposition";
import {RecipeServingType} from "@/models/Recipes/RecipeServingType";
import {RecipeCalculateSubrecipe} from "@/models/Recipes/RecipeCalculate";
import {Subrecipe} from "@/models/Recipes/Subrecipe";
import {RecipeCompositionMeasurement} from "@/models/Recipes/RecipeCompositionMeasurement";
import {ServingSizeMeasurement} from "@/models/Measurement/ServingSizeMeasurement";
import {LabelTypeMetadata} from "@/models/NutritionLabels/LabelTypeMetadata";
import VCalculationMethodModal from "@/components/vCalculationMethodModal.vue";
import {LocalizationState} from "@/store/modules/LocalizationModule";
import {CalculationMethodModalData} from "@/models/Recipes/CalculationMethodModalData";
import {Dish} from "@/models/Dishes/Dish";
import {AccountState} from "@/store/modules/AccountModule";
import {CaloriesCalculationMethod} from "@/models/Recipes/CaloriesCalculationMethod";
import {DailyValueCalculationMethod} from "@/models/Recipes/DailyValueCalculationMethod";
import {PrintRecipeDetails} from "@/models/Recipes/PrintRecipeDetails";
import VRecipePrintout from "@/components/vRecipePrintout.vue";
import NutritionLabelRepository from "@/utils/API/NutritionLabelRepository";
import { NutritionCountry } from "@/models/NutritionLabels/NutritionCountry";
import VUnroundedValuesWarning from "@/components/vUnroundedValuesWarning.vue";
import {createDefaultProductType, RecipeProductType} from "@/models/Recipes/RecipeProductType";
import {PromiseReject, PromiseResolve} from "@/typings/common";
import {Snotify} from "vue-snotify";
import { RecipeMetadata } from "@/models/Recipes/RecipeMetadata";
import vIconWithTooltip from "@/components/vIconWithTooltip.vue";
import VFoPWarning from "@/components/vFoPWarning.vue";
import VClaimsAndStatementsModal from "@/components/vClaimsAndStatementsModal.vue";
import VEnabledClaimsList from "@/components/vEnabledClaimsList.vue";
import ClaimsRepository from "@/utils/API/ClaimsRepository";
import VPreviewLabel from "@/components/vPreviewLabel.vue";
import {RoundingRules} from "@/models/NutritionLabels/RoundingRules";
import VBanner from "@/components/vBanner.vue";
import {CreateRecipe} from "@/models/Recipes/CreateRecipe";
import {UpdateRecipe} from "@/models/Recipes/UpdateRecipe";
import {ErrorResponse} from "@/utils/API/ResultTypes/ErrorResponse";
import { CanadaFOPHighInMetadata } from "@/models/NutritionLabels/CanadaFOPHighInMetadata";
import {NutritionLabelCountry} from "@/models/NutritionLabels/NutritionLabelCountry";
import VUpsellModal from "@/components/modals/marketing/vUpsellModal.vue";

const vExportLabel = () => import("@/components/vExportLabel.vue");

interface DishesManagementState {
  count: number;
  totalCount: number;
  page: number;
  pageSize: number;
  filters: Filter[];
  orders: Ordering;
  loading: boolean;
  recipes: Recipe[];

  dish: Dish
}

@Component({
  components: {
    VUpsellModal,
    VBanner,
    VPreviewLabel,
    VFoPWarning,
    VUnroundedValuesWarning,
    VRecipePrintout,
    VCalculationMethodModal,
    vBreadcrumbs,
    vNutritionLabel,
    vModal,
    vPreLoader,
    vExportLabel,
    vSelect,
    vSelectFx,
    vIconWithTooltip,
    VClaimsAndStatementsModal,
    VEnabledClaimsList
  },
  methods: {
    debounce
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountState: (state: any) => state.AccountModule,
    }),
  },
})
class DishesManagement extends Vue {
  localizationState: LocalizationState;
  accountState: AccountState;

  DailyValueCalculationMethod = DailyValueCalculationMethod;

  calculationMethodModal: CalculationMethodModalData = new CalculationMethodModalData();
  roundingRuleIndex: number = 0;

  selectedRoundingRule: RoundingRules = { id: 0, name: 'FDA' };
  nutritionRules = [
    { id: 0, name: 'FDA' },
    { id: 1, name: 'FSIS (2025)' },
    { id: 2, name: 'FSIS (2007)' },
  ];

  recipeMetadata: RecipeMetadata = {
    recipeId: 0,
    countryId: 0,
    referenceAmount: 0,
    productTypeId: 0,
    roundingRuleIndex: 0,
    isSupplementLabel: false,
  };

  dishesManagementState: DishesManagementState = {
    count: 0,
    totalCount: 0,
    page: 1,
    pageSize: 5,
    filters: [],
    orders: { field: "Name", orderType: OrderType.Asc },
    loading: false,
    recipes: [],
    dish: {
      id: 0,
      name: "",
      description: "",
      instructions: null,
      servings: 1,
      recipeCategory: null,
      ingredients: [],
      nutritionAnalysis: new Nutritions(
        [],
        'g',
        0,
        0,
        0,
        0,
        0,
        null,
        null,
        null,
        null
      ),
      childRecipes: [],
      caloriesCalculationMethod: CaloriesCalculationMethod.Summation,
      dailyValueCalculationMethod: DailyValueCalculationMethod.Rounded,
      proteinDvCalculationMethod: 0,
      pdcaasScore: 1,
      nutritionCountry: null,
      referenceAmount: 0,
      recipeProductType: {
        id: 0,
        name: "",
        productType: 0,
        countryId: 0
      },
      productTypeId: 0
    }
  };

  showDeleteDishModal = false;
  showExportLabelModal = false;
  isDirty: boolean = true; // Tracks the saved state of the dish.

  enabledClaims: string[] = [];

  // General reference variables
  subrecipeServingSizes: RecipeCompositionMeasurement[] = [];
  subrecipeServingMeasurements: ServingSizeMeasurement[] = [];
  foodProductTypes: RecipeProductType[] = [];
  nutritionCountries: NutritionCountry[] = [];
  LabelType = LabelTypeMetadata;
  showClaimModal = false;
  isDisplayFop: boolean = true;
  isSupplementLabelLoaded: boolean = false;

  printRecipeMeta: PrintRecipeDetails = {
    ingredients: [],
    brandingLogo: null,
    statementsAndClaims: []
  };

  showTrialUpsellModal: boolean = false;

  closeTrialUpsellModal() {
    this.showTrialUpsellModal = false;
  }

  /**
   * Determines the default label type based on the recipe metadata properties.
   * Evaluates conditions such as whether the label corresponds to supplement facts,
   * FSIS requirements, or standard nutrition facts.
   *
   * @return {LabelTypeMetadata} The default label type, which can be SupplementFacts, FSIS, or NutritionFacts.
   */
  get defaultLabelType() {
    if (this.recipeMetadata.isSupplementLabel) {
      return LabelTypeMetadata.SupplementFacts;
    }

    if (this.recipeMetadata.roundingRuleIndex != 0 && this.recipeMetadata.countryId == NutritionLabelCountry.US) {
      return LabelTypeMetadata.FSIS;
    }

    return LabelTypeMetadata.NutritionFacts;
  }

  /**
   * Retrieves the name of the country associated with the nutrition data.
   *
   * @return {string | null} The name of the country if available, or null if no country information exists.
   */
  get countryName(): string | null {
    if (this.dishesManagementState.dish.nutritionCountry) {
      return this.dishesManagementState.dish.nutritionCountry.name;
    }

    return null;
  }

  /**
   * Retrieves the id of the country associated with the nutrition data.
   *
   * @return {number | null} The id of the country if available, or null if no country information exists.
   */
  get countryId(): number | null {
    if (this.dishesManagementState.dish.nutritionCountry) {
      return this.dishesManagementState.dish.nutritionCountry.id;
    }

    return null;
  }

  get isEmptyRecipe(){
    return this.dishesManagementState.dish.childRecipes.length == 0;
  }

  get highInMetadata(){
    if(this.dishesManagementState.dish.nutritionAnalysis.additionalLabelInformation){
      return this.dishesManagementState.dish.nutritionAnalysis.additionalLabelInformation[this.LabelType.FrontOfPack].highInMetadata;
    }
    else{
      CanadaFOPHighInMetadata.None;
    }
  }

  // Watches
  @Watch("caloriesCalculationMethod")
  onCaloriesCalculationMethodChange() {
    this.calculateNutritions();
  }

  get caloriesCalculationMethod() {
    return this.calculationMethodModal.selectedCalculationMethod;
  }

  tryInitialSave() {
    if (!this.dishesManagementState.dish.id && this.dishesManagementState.dish.name) {
      this.submit();
    }
  }

  initSearch() {
    let selector = <any>$("#searchRecipes");
    if (selector.hasClass("select2-hidden-accessible")) {
      selector.html("").select2();
    }
    let searchTerm = "";
    selector
      .select2({
        allowClear: false,
        placeholder: "Search Recipes",
        minimumInputLength: 3,
        ajax: {
          delay: 500,
          transport: (params: any, success: any) => {
            RecipesRepository.filterRecipesNames(
              params.data.term ? params.data.term : ""
            ).then((response) => {
              success(response);
            });
            searchTerm = params.data.term;
          },
          processResults: (response: any) => {
            let data: any[] = [];

            response.data.result.map((c: FilterRecipesNames) => {
              let children: any = [];

              if (c.recipes && c.recipes.length) {
                data.push({ id: `cat-${c.id}`, text: getBoldString(c.name, "") });

                c.recipes.map((recipe: Recipe) => {
                  children.push({ id: recipe.id, text: getBoldString(recipe.name, searchTerm) });
                });
              }

              data.push({ children: children });
            });

            return { results: data };
          },
        },
        escapeMarkup: function(element:string){
          return element;
        },
      })
      .on("select2:select", (eventData: any) => {
        let value: string = <string>eventData.target.value;
        if (value.startsWith("cat")) {
          let parts = value.split("-");
          let categoryId = parts[1];

          this.dishesManagementState.filters = [
            {
              name: "RecipeCategoryId",
              value: categoryId,
            },
          ];
        }
        else {
          this.dishesManagementState.filters = [{ name: "Id", value: value }];
          this.dishesManagementState.page = 1;
        }
        this.requestRecipes(
          new ListRequest({
            page: this.dishesManagementState.page,
            pageSize: this.dishesManagementState.pageSize,
            filters: this.dishesManagementState.filters,
            orders: [this.dishesManagementState.orders],
          })
        );
      });
  }

  setDirtyState() {
    this.isDirty = true;
  }

  clearDirtyState() {
    this.isDirty = false;
  }

  async fetchPackageType(countryId: number = 0){
    await RecipesRepository.getProductTypesByCountry(countryId).then((response) => {
      if(response){
        this.foodProductTypes = [
          ...response.data,
        ];

        let productType = response.data.find(
          (x: RecipeProductType) => x.id == this.dishesManagementState.dish.productTypeId
        );

        if(productType) {
          this.dishesManagementState.dish.recipeProductType = productType;
        }
      }
      else{
        this.foodProductTypes = [];
        this.dishesManagementState.dish.recipeProductType = createDefaultProductType();
      }
    });
  }

  selectProductType(productType: RecipeProductType) {
    this.dishesManagementState.dish.recipeProductType = productType;
    this.calculateNutritions();
    this.setDirtyState();
  }

  openClaimsModal(){
    this.showClaimModal = true;
  }

  async showExportLabel() {
    if (this.accountState.userDetails.isTrial) {
      this.showTrialUpsellModal = true;
      return;
    }

    await this.updatePrintMeta();
    this.showExportLabelModal = true;
  }

  async updatePrintMeta() {
    const response = await RecipesRepository.getPrintRecipeDetails(this.dishesManagementState.dish.id);
    this.printRecipeMeta = response.data;
  }

  async printRecipe() {
    if (this.accountState.userDetails.isTrial) {
      this.showTrialUpsellModal = true;
      return;
    }

    await this.updatePrintMeta();
    await new Promise((res) => setTimeout(res, 500));
    window.print();
  }

  editRecipe(id: number) {
    let routeData = this.$router.resolve({
      name: "recipesEdit",
      params: { id: `${id}` },
    });

    window.open(routeData.href, "_blank");
  }

  addRecipeToDish(recipeId: number) {
    const recipe = this.dishesManagementState.recipes.find((r) => r.id === recipeId);

    let subrecipe: Subrecipe = {
      id: recipe.id,
      recipe,
      isAddedSugar: false,
      quantity: 1,
      recipeCompositionMeasurementId: 1,
      hasVolumeUnitOfMeasurement: recipe.volume !== null,
      weightPerServing: recipe.weightPerServing,
      foodGroupClassificationId: null,
    };

    this.dishesManagementState.dish.childRecipes.push(subrecipe);
    if(this.dishesManagementState.dish.id){
      this.calculateNutritions();
    }

    this.setDirtyState();
  }

  onRecipeQuantityAdjust() {
    this.calculateNutritions();
    this.setDirtyState();
  }

  onRecipeMeasureSelect(event: { id: number, name: string }, subrecipe: Subrecipe) {
    this.selectSubRecipeMeasurement(event, subrecipe);
    this.setDirtyState();
    this.calculateNutritions();
  }

  async updateRecipeCountryMetadata(selectedCountry: NutritionCountry, isSupplementLabel: boolean = false) {
    this.dishesManagementState.dish.nutritionCountry = { ...selectedCountry };
    this.recipeMetadata.isSupplementLabel = isSupplementLabel;

    let dishMetadata: RecipeMetadata = {
      recipeId: this.dishesManagementState.dish.id,
      countryId: this.countryId,
      referenceAmount: this.dishesManagementState.dish.referenceAmount,
      productTypeId: this.dishesManagementState.dish.productTypeId,
      roundingRuleIndex: 0,
      isSupplementLabel: isSupplementLabel
    };

    const response = await RecipesRepository.updateRecipeCountry(dishMetadata);

    this.dishesManagementState.dish.nutritionAnalysis = mapNutritions(response.data);
    this.isSupplementLabelLoaded = isSupplementLabel;
  }

  async updateRecipeRoundingRulesMetadata(selectedRule: RoundingRules) {
    this.recipeMetadata.roundingRuleIndex = selectedRule.id;
    this.dishesManagementState.loading = true;

    RecipesRepository.updateRecipeRoundingRule({
      ruleIndex: selectedRule.id,
      recipeId: this.dishesManagementState.dish.id,
      countryId: this.countryId
    })
      .then((response: any) => {
        if (response) {
          this.mapCombinedRecipeDetails(response.data);
        }
      })

      .finally(() => {
        this.dishesManagementState.loading = false;
      });
  }

  async getRecipeMetaData(recipeId: number) {
    const response = await RecipesRepository.getRecipeMetaData(recipeId);
    this.recipeMetadata = response.data;
    const ruleIndex = response.data.roundingRuleIndex || 0;
    const selectedRule = this.nutritionRules.find((rule) => rule.id === ruleIndex);

    if (selectedRule) {
      this.selectedRoundingRule = selectedRule;
    }

    if(this.recipeMetadata.isSupplementLabel) {
      this.isSupplementLabelLoaded = true;
    }

    this.recipeMetadata.roundingRuleIndex = ruleIndex;
  }

  removeRecipefromDish(recipeId: number) {
    let reference = this.dishesManagementState.dish.childRecipes.find(
      (r) => r.id === recipeId
    );

    if (reference) {
      this.dishesManagementState.dish.childRecipes.splice(
        this.dishesManagementState.dish.childRecipes.indexOf(reference),
        1
      );
    }

    if(this.dishesManagementState.dish.id){
      this.calculateNutritions();
    }

    this.setDirtyState();
  }

  order(type: string) {
    let orderType = parseInt(type);
    if (orderType === OrderType.Desc) {
      this.dishesManagementState.orders.orderType = OrderType.Desc;
    }
    else {
      this.dishesManagementState.orders.orderType = OrderType.Asc;
    }

    this.requestRecipes(
      new ListRequest({
        page: this.dishesManagementState.page,
        pageSize: this.dishesManagementState.pageSize,
        filters: this.dishesManagementState.filters,
        orders: [this.dishesManagementState.orders],
      })
    );
  }

  clear(obj: any) {
    if (obj === null) {
      this.resetSearchRecipes();
    }
  }

  requestRecipes(listRequest: ListRequest) {
    this.dishesManagementState.loading = true;

    RecipesRepository.listRecipes(listRequest)
      .then((response) => {
        this.dishesManagementState.recipes = response.data.result;
        this.dishesManagementState.recipes.map((recipe: any) => {
          recipe.nutritionAnalysis = mapNutritions(recipe.nutritionAnalysis);
        });
        this.dishesManagementState.count = response.data.count;
        this.dishesManagementState.totalCount = response.data.totalCount;
        this.dishesManagementState.loading = false;
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(error.response.data.errorMessages[0].userVisibleMessage);
        }

        else {
          this.$snotify.error(this.translations.get("network-error"));
        }
      });
  }

  get disabledNext() {
    return (
      Math.ceil(
        this.dishesManagementState.totalCount /
          this.dishesManagementState.pageSize
      ) === this.dishesManagementState.page
    );
  }

  get showControls() {
    return (
      this.dishesManagementState.totalCount > this.dishesManagementState.count
    );
  }

  next() {
    if (!this.disabledNext) {
      this.dishesManagementState.page = this.dishesManagementState.page + 1;

      this.requestRecipes(
        new ListRequest({
          page: this.dishesManagementState.page,
          pageSize: this.dishesManagementState.pageSize,
          filters: this.dishesManagementState.filters,
          orders: [this.dishesManagementState.orders],
        })
      );
    }
  }

  prev() {
    if (this.dishesManagementState.page > 1) {
      this.dishesManagementState.page = this.dishesManagementState.page - 1;

      this.requestRecipes(
        new ListRequest({
          page: this.dishesManagementState.page,
          pageSize: this.dishesManagementState.pageSize,
          filters: this.dishesManagementState.filters,
          orders: [this.dishesManagementState.orders],
        })
      );
    }
  }

  deleteDish() {
    //@ts-ignore
    this.$snotify.async(
      this.translations.get("dish_toastr_deleting"),
      () =>
        new Promise((resolve, reject) => {
          DishesRepository.deleteDishes({
            dishIds: [this.dishesManagementState.dish.id],
          })
            .then(() => {
              resolve({
                body: this.translations.get("dish_toastr_deleted"),
              });
              this.$router.push({ name: "dishes" });
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("toastr_network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 3000,
        closeOnClick: true,
      }
    );
    this.resetState();
  }

  submit(shouldRedirect: boolean = false) {
    let recipeCompositions: Partial<RecipeComposition>[] = this.dishesManagementState.dish.childRecipes.map(
      (subrecipe: Subrecipe) => {
        return {
          childRecipeId: subrecipe.recipe.id,
          parentRecipeId: this.dishesManagementState.dish.id,
          quantity: subrecipe.quantity,
          recipeCompositionMeasurementId: subrecipe.recipeCompositionMeasurementId
        };
      }
    );

    this.$validator.validateAll().then((result) => {
      if (result) {
        this.$snotify.async(
          this.translations.get("dish_toastr_saving"),
          () => new Promise((resolve, reject) => {
            if (this.$route.name === "dishesEdit") {
              this.handleUpdateDish(resolve, reject, recipeCompositions, shouldRedirect);
            }

            else if (this.$route.name === "dishesAdd") {
              this.handleCreateDish(resolve, reject, recipeCompositions, shouldRedirect);
            }
          }),
          {
            timeout: 3000,
            closeOnClick: true,
          }
        );
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  handleUpdateDish(
    resolve: PromiseResolve<Snotify>,
    reject: PromiseReject,
    recipeCompositions: Partial<RecipeComposition>[],
    shouldRedirect: boolean
  ) {
    const productType = this.dishesManagementState.dish.recipeProductType
      ? this.dishesManagementState.dish.recipeProductType
      : createDefaultProductType();

    let updateRecipeDto: UpdateRecipe = {
      recipes: recipeCompositions.map((rc) => ({
        recipeId: rc.childRecipeId,
        quantity: rc.quantity,
        recipeCompositionMeasurementId: rc.recipeCompositionMeasurementId,
        isAddedSugar: rc.isAddedSugar,
        foodGroupClassificationId: rc.foodGroupClassificationId,
      })),
      ingredients: [],
      description: this.dishesManagementState.dish.description,
      recipeCategoryId: null,
      name: this.dishesManagementState.dish.name,
      recipeId: this.dishesManagementState.dish.id,
      instructions: this.dishesManagementState.dish.instructions,
      recipeServingType: RecipeServingType.Servings,
      servingCount: 1,
      servingQuantityInUnitOfMeasurement: null,
      servingUnitOfMeasurement: null,
      adjustedAddedSugarsValue: 0,
      nutritionCountry: this.countryId,
      referenceAmount: this.dishesManagementState.dish.referenceAmount,
      productTypeId: productType.id,
      nutrientTypesToCalculate: this.dishesManagementState.dish.nutritionCountry
        ? this.recipeMetadata.isSupplementLabel ? null : getPreviewNutrientsForCountry(
          this.dishesManagementState.dish.nutritionCountry.id)
        : null,
    };

    RecipesRepository.updateRecipe(updateRecipeDto)
      .then((response) => {
        this.mapCombinedRecipeDetails(response.data.result);
        this.clearDirtyState();

        if (shouldRedirect) {
          this.$router.push({ name: "dishes" });
        }

        resolve({
          body: this.translations.get("dish_toastr_saved"),
        });
      })
      .catch((error) => {
        let message = "";
        if (error.response) {
          message =
            error.response.data.errorMessages[0].userVisibleMessage;
        }

        else {
          message = this.translations.get("toastr_network_error");
        }
        reject({ body: message });
      });
  }

  handleCreateDish(
    resolve: PromiseResolve<Snotify>,
    reject: PromiseReject,
    recipeCompositions: Partial<RecipeComposition>[],
    shouldRedirect: boolean
  ) {
    const productType = this.dishesManagementState.dish.recipeProductType
      ? this.dishesManagementState.dish.recipeProductType
      : createDefaultProductType();

    let createRecipeDto: CreateRecipe = {
      recipes: recipeCompositions.map((rc) => ({
        recipeId: rc.childRecipeId,
        quantity: rc.quantity,
        recipeCompositionMeasurementId: rc.recipeCompositionMeasurementId,
        isAddedSugar: rc.isAddedSugar,
        foodGroupClassificationId: rc.foodGroupClassificationId
      })),
      ingredients: [],
      description: this.dishesManagementState.dish.description,
      recipeCategoryId: null,
      name: this.dishesManagementState.dish.name,
      instructions: null,
      servingCount: 1,
      adjustedAddedSugarsValue: 0,
      isCombinedRecipe: true,
      nutritionCountry: this.countryId,
      referenceAmount: this.dishesManagementState.dish.referenceAmount,
      productTypeId: productType.id
    };

    RecipesRepository.createRecipe(createRecipeDto)
      .then((response) => {
        this.$router.push({
          name: "dishesEdit",
          params: { id: response.data.id.toString() },
        });

        this.mapCombinedRecipeDetails(response.data);
        this.clearDirtyState();

        if (shouldRedirect === true) {
          this.$router.push({ name: "dishes" });
        }

        resolve({
          body: this.translations.get("dish_toastr_saved"),
        });
      })
      .catch((error: ErrorResponse) => {
        let message;
        if (error.response) {
          message = error.response.data.message;
        }

        else {
          message = "Unknown error when creating dish.";
        }

        reject({ body: message });
      });
  }

  calculateNutritions() {
    let recipeIds = this.dishesManagementState.dish.childRecipes.map((subrecipe: Subrecipe): RecipeCalculateSubrecipe => {
      return {
        recipeId: subrecipe.recipe.id,
        recipeCompositionMeasurementId: subrecipe.recipeCompositionMeasurementId,
        quantity: subrecipe.quantity,
        isAddedSugar: false,
        foodGroupClassificationId: 0
      };
    });

    new Promise((resolve, reject) => {
      DishesRepository.calculate({
        recipeId: this.dishesManagementState.dish.id,
        servings: 1,
        ingredients: [],
        recipeServingType: RecipeServingType.Servings,
        recipes: recipeIds,
        caloriesCalculationMethod: this.calculationMethodModal.selectedCalculationMethod,
        dailyValueCalculationMethod: this.calculationMethodModal.selectedDailyValueCalculation,
        referenceAmount : this.dishesManagementState.dish.referenceAmount,
        recipeProductType : this.dishesManagementState.dish.recipeProductType,
        ruleIndex: this.recipeMetadata.roundingRuleIndex
      })
        .then((response) => {
          this.dishesManagementState.dish.nutritionAnalysis =
            mapNutritions(response.data.result);
          resolve({});
        })
        .catch((error) => {
          let message = "";
          if (error.response) {
            message = error.response.data.errorMessages[0].userVisibleMessage;
          }

          else {
            message = this.translations.get("toastr_network_error");
          }

          reject({ body: message });
        });
    });
  }

  resetSearchRecipes() {
    RecipesRepository.listRecipes(
      new ListRequest({
        page: this.dishesManagementState.page,
        pageSize: this.dishesManagementState.pageSize,
      })
    )
      .then((response) => {
        this.dishesManagementState.recipes = response.data.result;
        this.dishesManagementState.recipes.map((recipe: any) => {
          recipe.nutritionAnalysis = mapNutritions(recipe.nutritionAnalysis);
        });
        this.dishesManagementState.count = response.data.count;
        this.dishesManagementState.totalCount = response.data.totalCount;
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }

  resetState() {
    this.showDeleteDishModal = false;
    this.showExportLabelModal = false;
  }

  async fetchEnabledClaims() {
    // If we don't have a country set, we shouldn't fire the request
    if (this.countryId != null) {
      const response = await ClaimsRepository.getEnabledClaimsForRecipe(
        this.dishesManagementState.dish.id,
        this.countryId
      );

      if (response && response.data) {
        this.enabledClaims = response.data;
      }
    }
  }

  getSubrecipeMeasurementByCompositionMeasurementId(compositionMeasurementId: number) {
    return this.subrecipeServingSizes.find(
      (rcm: RecipeCompositionMeasurement) => rcm.id === compositionMeasurementId
    )!.measurement;
  }

  fetchSubrecipeServingSizes() {
    RecipesRepository.getRecipeCompositionMeasurements().then((response) => {
      this.subrecipeServingSizes = response.data.result;

      this.subrecipeServingMeasurements = this.subrecipeServingSizes.map(
        (measurement: RecipeCompositionMeasurement) => measurement.measurement
      );
    });
  }

  updateReferenceAmount(){
    this.dishesManagementState.dish.referenceAmount = this.dishesManagementState.dish.referenceAmount.toString() != "" ? this.dishesManagementState.dish.referenceAmount : 0;

    this.setDirtyState();
  }

  selectSubRecipeMeasurement(
    measurement: { id: number; name: string },
    subRecipe: any
  ) {
    // Find the RecipeCompositionMeasurement object that corresponds to the selected measurement
    const measurementObj = this.subrecipeServingSizes.find(
      (rcm: RecipeCompositionMeasurement) => rcm.measurement.id === measurement.id
    );

    subRecipe.recipeCompositionMeasurementId = measurementObj.id;
    this.calculateNutritions();
  }

  async fetchNutritionCountries(){
    const response = await NutritionLabelRepository.listNutritionCountries();
    this.nutritionCountries = response.data.result;
  }

  get translations() {
    return this.localizationState.translations;
  }

  mounted() {
    Vue.nextTick(() => {
      (<any>window).$.Pages.init();
      this.initSearch();
    });
  }

  async getDishDetails() {
    const response = await RecipesRepository.getRecipeDetails(parseInt(this.$route.params.id), true);

    if (!response.data) {
      this.isDirty = true;
      await this.$router.push({ name: "dishesAdd" });
    }

    try {
      await this.mapCombinedRecipeDetails(response.data);
    }

    catch {
      this.isDirty = true;
      await this.$router.push({ name: "dishesAdd" });
    }
  }

  async mapCombinedRecipeDetails(recipeResponse: Dish) {
    this.dishesManagementState.dish = {
      ...this.dishesManagementState.dish,
      ...recipeResponse
    };
    this.dishesManagementState.dish.servings = 1;
    this.dishesManagementState.dish.instructions = null;
    this.dishesManagementState.dish.recipeCategory = null;
    this.dishesManagementState.dish.nutritionAnalysis = mapNutritions(
      this.dishesManagementState.dish.nutritionAnalysis
    );
    this.dishesManagementState.dish.referenceAmount = recipeResponse.referenceAmount;

    await this.fillNutritionCountry(recipeResponse.nutritionCountry);
    await this.getRecipeMetaData(recipeResponse.id);

    if(this.foodProductTypes.length > 0) {
      this.dishesManagementState.dish.recipeProductType = this.foodProductTypes.find(
        (x) => x.id === recipeResponse.productTypeId
      ) || createDefaultProductType();
    }

    this.calculationMethodModal.selectedCalculationMethod = recipeResponse.caloriesCalculationMethod;
    this.calculationMethodModal.selectedDailyValueCalculation = recipeResponse.dailyValueCalculationMethod;
    this.calculationMethodModal.selectedProteinDailyValueCalculation = recipeResponse.proteinDvCalculationMethod;
    this.calculationMethodModal.pdcaasScore = recipeResponse.pdcaasScore;

    await this.fetchEnabledClaims();

    this.dishesManagementState.loading = false;
  }

  private async fillNutritionCountry(nutritionCountryId: any) {
    const nutritionCountry = this.nutritionCountries.find((x) => x.id === nutritionCountryId);
    if (nutritionCountry) {
      this.dishesManagementState.dish.nutritionCountry = nutritionCountry;
      await this.fetchPackageType(nutritionCountry.id);
    }
  }

  async created() {
    await this.fetchNutritionCountries();

    this.$store.dispatch(
      TRANSLATIONS_FETCH,
      {
        category: "validationrules",
        collections: ["recipes"],
      }
    );

    this.$store
      .dispatch(TRANSLATIONS_FETCH, {
        category: "pages",
        collections: [
          "common",
          "recipes",
          "dishes",
          "nutrition-labels",
          "cautionary"
        ],
      })
      .then(() => {
        if (this.$route.name === "dishesEdit") {
          document.title = this.translations.get("dish_title_edit") + " " + this.translations.get("meta_title_postfix");
        }

        else {
          document.title = this.translations.get("dish_title_add") + " " + this.translations.get("meta_title_postfix");
          this.fetchPackageType();
        }
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });

    this.fetchSubrecipeServingSizes();
    if (this.$route.name === "dishesEdit") {
      this.dishesManagementState.loading = true;
      await this.getDishDetails();
    }

    this.resetSearchRecipes();
  }
}
export default DishesManagement;
